import { firestore } from '../firebase';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { subscriptionTiers } from '../stripeConfig';

/**
 * Subscription Manager
 * Handles checking and caching subscription status for users
 */
class SubscriptionManager {
  constructor() {
    // Cache subscription status for 5 minutes to reduce Firestore reads
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Clear cache for a specific email
   */
  clearCache(email) {
    if (email) {
      this.cache.delete(email);
    }
  }

  /**
   * Clear entire cache
   */
  clearAllCache() {
    this.cache.clear();
  }

  /**
   * Get user subscription tier based on their email
   * @param {string} email - User's email address from Spotify
   * @returns {Promise<Object>} Tier information (free or premium)
   */
  async getUserTier(email) {
    if (!email) {
      console.log('No email provided, defaulting to free tier');
      return {
        ...subscriptionTiers.free,
        tier: 'free',
        subscriptionStatus: 'none'
      };
    }

    // Check cache first
    const cached = this.cache.get(email);
    if (cached && cached.timestamp > Date.now() - this.cacheTimeout) {
      console.log(`Using cached subscription status for ${email}`);
      return cached.data;
    }

    try {
      // Query Firestore for subscription status
      const subscriptionDoc = await firestore
        .collection('subscriptions')
        .doc(email)
        .get();

      let tierData;

      if (subscriptionDoc.exists) {
        const subscription = subscriptionDoc.data();

        // Check if subscription is active
        if (subscription.subscriptionStatus === 'active' &&
            subscription.tier === 'premium') {
          console.log(`User ${email} is a premium subscriber`);
          tierData = {
            ...subscriptionTiers.premium,
            tier: 'premium',
            subscriptionStatus: subscription.subscriptionStatus,
            subscriptionId: subscription.subscriptionId,
            startedAt: subscription.startedAt,
            cancelAt: subscription.cancelAt || null
          };
        } else if (subscription.subscriptionStatus === 'past_due') {
          // Give grace period for past due payments
          console.log(`User ${email} subscription is past due, treating as premium with warning`);
          tierData = {
            ...subscriptionTiers.premium,
            tier: 'premium',
            subscriptionStatus: 'past_due',
            showPaymentWarning: true,
            subscriptionId: subscription.subscriptionId,
            cancelAt: subscription.cancelAt || null
          };
        } else {
          // Subscription exists but is not active
          console.log(`User ${email} has inactive subscription, using free tier`);
          tierData = {
            ...subscriptionTiers.free,
            tier: 'free',
            subscriptionStatus: subscription.subscriptionStatus || 'canceled',
            cancelAt: null
          };
        }
      } else {
        // No subscription record found
        console.log(`No subscription found for ${email}, using free tier`);
        tierData = {
          ...subscriptionTiers.free,
          tier: 'free',
          subscriptionStatus: 'none'
        };
      }

      // Cache the result
      this.cache.set(email, {
        data: tierData,
        timestamp: Date.now()
      });

      return tierData;
    } catch (error) {
      console.error('Error checking subscription status:', error);

      // On error, default to free tier but don't cache
      return {
        ...subscriptionTiers.free,
        tier: 'free',
        subscriptionStatus: 'error',
        error: error.message
      };
    }
  }

  /**
   * Check if user has premium access
   * @param {string} email - User's email address
   * @returns {Promise<boolean>} True if user has premium access
   */
  async isPremium(email) {
    const tier = await this.getUserTier(email);
    return tier.tier === 'premium';
  }

  /**
   * Get subscription configuration for a user
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Configuration object with all tier settings
   */
  async getSubscriptionConfig(email) {
    const tierData = await this.getUserTier(email);

    return {
      tier: tierData.tier,
      listeningTimeRequired: tierData.listeningTimeRequired,
      maxVisibleRatings: tierData.maxVisibleRatings,
      songsToRateBeforeUpload: tierData.songsToRateBeforeUpload,
      subscriptionStatus: tierData.subscriptionStatus,
      showPaymentWarning: tierData.showPaymentWarning || false,
      showUpgradePrompts: tierData.tier === 'free',
      cancelAt: tierData.cancelAt || null
    };
  }

  /**
   * Subscribe to real-time subscription status updates
   * @param {string} email - User's email address
   * @param {Function} callback - Callback function when subscription status changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToStatusUpdates(email, callback) {
    if (!email) {
      console.warn('No email provided for subscription updates');
      return () => {}; // Return empty unsubscribe function
    }

    // Use v9 Firebase SDK for real-time updates
    const db = getFirestore();
    const subscriptionDocRef = doc(db, 'subscriptions', email);

    const unsubscribe = onSnapshot(
      subscriptionDocRef,
      (docSnapshot) => {
        // Clear cache when subscription changes
        this.clearCache(email);

        // Get updated tier information
        this.getUserTier(email).then(tierData => {
          callback(tierData);
        });
      },
      (error) => {
        console.error('Error listening to subscription updates:', error);
      }
    );

    return unsubscribe;
  }
}

// Create singleton instance
const subscriptionManager = new SubscriptionManager();

export default subscriptionManager;